import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getSellerShopName } from '../../util/userHelpers';
import { salePercentOff } from '../../util/sales';
import { Avatar, IconLocation, IconReviewStar, NamedLink, UserBadgeList } from '../../components';

import css from './ShopsPage.module.css';

const SHELFY_OFFICIAL_USER_ID = '6a7dd91a-66d6-4a6e-afde-d158d9514132';

/** Displays a seller and a small preview of the seller's published listings. */
const ShopCard = ({ shop, provinceLabel, sales = [], getSpecialtyLabel }) => {
  const location = useLocation();
  const { seller, listings } = shop;
  const { displayName, publicData = {} } = seller.attributes.profile || {};
  const shopName = getSellerShopName(seller, displayName) || displayName;
  const province = provinceLabel || publicData.province || publicData.ships_from;
  const isShelfyOfficial = seller.id.uuid === SHELFY_OFFICIAL_USER_ID;
  const saleListingIds = new Set(sales.map(sale => sale.listingId));
  const saleListings = listings.filter(listing => saleListingIds.has(listing.id.uuid));
  const salePercent = saleListings.length
    ? salePercentOff(
        saleListings[0].attributes?.price,
        sales.find(sale => sale.listingId === saleListings[0].id.uuid)
      )
    : null;
  const allSpecialties = (publicData.shopSpecialties || []).map(getSpecialtyLabel);
  const specialties = allSpecialties.slice(0, 3);
  const moreSpecialties = Math.max(0, allSpecialties.length - specialties.length);
  const bundleDiscount = publicData.bundleDiscountEnabled ? publicData.bundleDiscountPercent : null;

  return (
    <NamedLink
      className={css.shopCard}
      data-testid="shop-card"
      name="ShopPage"
      params={{ id: seller.id.uuid }}
      to={{
        state: {
          from: { pathname: location.pathname, search: location.search },
          backLabel: 'Back to Shops',
        },
      }}
      ariaLabel={`Visit ${shopName}`}
    >
      <div className={css.shopCardHeader}>
        <Avatar className={css.shopAvatar} user={seller} disableProfileLink />
        <div>
          <h2 className={css.shopName}>{shopName}</h2>
          <UserBadgeList className={css.shopBadges} user={seller} compact />
          {province ? (
            <div className={css.locationBlock}>
              <span className={css.shipsFrom}>Ships from</span>
              <span className={css.province}>
                <IconLocation className={css.locationIcon} />
                {province}
              </span>
            </div>
          ) : null}
          {isShelfyOfficial ? (
            // TEMP: visual preview for PageHop Official Shop rating layout.
            <div className={css.previewRating} aria-label="5.0 out of 5 stars">
              {Array.from({ length: 5 }).map((_, index) => (
                <IconReviewStar key={index} className={css.previewRatingStar} isFilled />
              ))}
              <span>5.0</span>
            </div>
          ) : null}
        </div>
      </div>
      <div className={css.previewGrid} aria-label={`${shopName} inventory`}>
        {listings.slice(0, 5).map(listing => {
          const image = listing.images?.[0];
          const url = image?.attributes?.variants?.['listing-card']?.url;
          return url ? (
            <img key={listing.id.uuid} className={css.previewImage} src={url} alt="" />
          ) : null;
        })}
      </div>
      <div className={css.featureRow}>
        <div className={css.promotions}>
          {saleListings.length ? (
            <span className={css.saleBadge}>
              {salePercent
                ? `${salePercent}% off ${
                    saleListings.length === listings.length ? 'shopwide' : 'selected listings'
                  }`
                : 'ON SALE'}
            </span>
          ) : null}
          {bundleDiscount ? (
            <span className={css.bundleBadge}>Save {bundleDiscount}% when you bundle</span>
          ) : null}
        </div>
        {specialties.length ? (
          <div className={css.specialties}>
            {specialties.map(specialty => (
              <span key={specialty}>{specialty}</span>
            ))}
            {moreSpecialties ? <span>+{moreSpecialties} more</span> : null}
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

ShopCard.propTypes = {
  shop: PropTypes.shape({
    seller: PropTypes.object.isRequired,
    listings: PropTypes.array.isRequired,
    reviews: PropTypes.array.isRequired,
  }).isRequired,
  provinceLabel: PropTypes.string,
  sales: PropTypes.array,
  getSpecialtyLabel: PropTypes.func,
};

export default ShopCard;
